import React from 'react';
import { usePageMeta } from '../utils/pageMeta';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useBranding } from '../contexts/BrandingContext';

const gold = '#C9A84C';

// Hide gracefully if an image hasn't been added to /public yet.
const hideOnError = (e) => { e.target.style.display = 'none'; };

export default function Directions() {
  const branding = useBranding();
  // Both map links are just the address encoded, so they follow it out of the
  // file rather than being configured separately and drifting from it.
  const ADDRESS = branding.facilityAddress;
  const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;
  const MAPS_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS)}`;
  usePageMeta('Directions to {facility} | {club}', 'Directions to {facility}, {address}. Arrival guidance from the north and south, across from Dunkin Donuts.');
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0D1E35', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 4 }}>
      <Box component="img" src="/horzlogo.png" alt="Torch Beach Volleyball"
        sx={{ maxWidth: 300, width: '75vw', cursor: 'pointer', mb: 3 }} onClick={() => navigate('/')} />

      <Box sx={{ width: '100%', maxWidth: 640 }}>
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: 3, color: gold, textAlign: 'center' }}>
          Directions
        </Typography>

        <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)', mb: 2 }}>
          <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.4rem', letterSpacing: 2, color: 'white' }}>
            {branding.facilityName}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{ADDRESS}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', mt: 0.5 }}>
            Across from Dunkin' Donuts
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(201,168,76,0.12)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(201,168,76,0.35)', '& .MuiAlert-icon': { color: gold } }}>
          Gate code: see your registration or confirmation email.
        </Alert>

        <Box component="img" src="/directions-map.jpg" alt={`${branding.facilityName} location map`} onError={hideOnError}
          sx={{ display: 'block', mx: 'auto', maxWidth: 320, width: '100%', borderRadius: 2, border: '1px solid rgba(201,168,76,0.35)', mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem', letterSpacing: 2, color: gold, mb: 1 }}>
            Arriving from the South
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, mb: 1.5 }}>
            Go through the light at Cypress Springs Parkway. Our courts are down the long driveway.
            We are 0.8 miles north of Curry Ford Road.
          </Typography>
          <Box component="img" src="/directions-from-south.png" alt="Approach arriving from the South" onError={hideOnError}
            sx={{ display: 'block', width: '100%', borderRadius: 2, border: '1px solid rgba(201,168,76,0.35)' }} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem', letterSpacing: 2, color: gold, mb: 1 }}>
            Arriving from the North
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, mb: 1.5 }}>
            Pass Cypress Meadows Lane. Our courts are down the long driveway.
            We are 2 miles south of the 408 on South Dean Road.
          </Typography>
          <Box component="img" src="/directions-from-north.png" alt="Approach arriving from the North" onError={hideOnError}
            sx={{ display: 'block', width: '100%', borderRadius: 2, border: '1px solid rgba(201,168,76,0.35)' }} />
        </Box>

        <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(201,168,76,0.35)', mb: 2 }}>
          <iframe
            title="Torch Beach Volleyball facility map"
            src={MAPS_EMBED}
            width="100%" height="360" style={{ border: 0, display: 'block' }}
            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </Box>

        <Button fullWidth variant="contained" href={MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer"
          sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700, py: 1.2, '&:hover': { backgroundColor: '#e0be6a' } }}>
          Get Directions in Google Maps
        </Button>

        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          <a href="/" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>← Back to home</a>
        </Typography>
      </Box>
    </Box>
  );
}
